/**
 * Ski-set market guard (AAS-5250)
 *
 * Clears the cart when the shopper's market changes and the cart held a ski set.
 *
 * WHAT WE FOUND (Shopify-side, verified)
 * Switching to a market that does NOT sell the ski leaves the set's cart line held
 * server-side but ABSENT from /cart.js — and it does not come back when the shopper returns
 * to a market that does sell it. So:
 *   - the line is unreachable from the storefront: not in the cart JSON -> no line key ->
 *     nothing to change, nothing to remove;
 *   - it still HOLDS the variant's inventory, so re-adding the set fails with
 *     "…is already sold out";
 *   - re-adding can never merge with the invisible line, because `_SetLinkId` is freshly
 *     random on every add, so the properties never match.
 * POST /cart/clear.js is the only lever that releases it.
 *
 * This is not ours. It reproduces with every one of our scripts blocked, and with a bare
 * cart line carrying any property that differs on re-add — a plain line with a `_test`
 * property is enough. Cart Transform is not involved.
 *
 * WHY NOT REPRICE INSTEAD
 * An earlier version synced the set's prices into the new market. It was replaced by removal
 * because the state it was repairing turns out to be unreachable from the storefront: there
 * is no line to reprice. Do not rebuild that path. What stays from it is the
 * `_price_currency` line property (so the backend can tell which market's cents are on the
 * line) and Liquid's inherently market-scoped pricing.
 *
 * WHY STATE COMPARISON AND NOT AN EVENT
 * There is no market-change event. The switcher submits a localization form and reloads, but
 * the market changes just as easily from a pasted locale-prefixed URL, an in-page link or a
 * redirect — none of which go near that control. Comparing the market seen NOW against the
 * market last seen catches every route in.
 *
 * WHY THE KEY IS handle|country|currency
 * DE -> AT is a real market change that never moves the currency, and keying on currency
 * alone would miss it completely.
 *
 * WHY "CART HOLDS A SET" IS RECORDED ON MUTATIONS, NOT ON LOAD
 * Add-to-cart is AJAX, so there is NO page load between configuring a set and switching
 * market. Recording only on load would leave the flag reading "no set" from before the add,
 * and the guard would sit out the exact flow it exists for: land on PDP -> add set -> switch
 * market. Hooking the fetch/XHR layer rather than subscribing to a cart-updated event also
 * covers the paths that never publish one (quick-add, wishlist, app-owned code) — they all
 * still hit a cart endpoint.
 */
(() => {
  if (window.Shopify && window.Shopify.designMode) return;

  const MARKET_KEY = 'skiSetMarketGuard:market';
  const HADSET_KEY = 'skiSetMarketGuard:hadSet';

  // Cart endpoints that can change what is in the cart. Deliberately NOT '/cart.js' — that
  // is what this file reads, and watching it would make the wrapper re-enter itself.
  const MUTATIONS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  /* ── storage: blocked storage degrades to a no-op, never throws ── */

  const read = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const write = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  };

  /* ── market identity ── */

  const marketKey = () => {
    const m = window.armadaMarket || {};
    const parts = [m.handle, m.country, m.currency].map(v => String(v || '').trim().toUpperCase());
    // All three blank means we cannot identify the market at all; treat as unknown rather
    // than as a market called "||", which every real market would then differ from.
    return parts.some(Boolean) ? parts.join('|') : '';
  };

  const root = () => (window.theme?.routes?.root_url || '').replace(/\/$/, '');
  const cartUrl = () => (window.routes && window.routes.cart_url) || `${root()}/cart`;

  const isMutation = (url) => MUTATIONS.some(ep => url.includes(ep));

  /* ── "does the cart hold a set?" ── */

  const lineIsSet = (item) => !!(item?.properties?._SetLinkId);

  // Page-load / clear reads must not go through the cart-error interceptor.
  // cart-error-toast.js stores the unwrapped fetch on window.theme.nativeFetch.
  const nativeFetch = (...args) => {
    const fetchImpl = window.theme?.nativeFetch || window.fetch.bind(window);
    return fetchImpl(...args);
  };

  // GET /cart.js 503s intermittently on this store (see ga4-datalayer.js). After a
  // successful add that extra read used to surface "error while updating your cart"
  // (AAS-5377). Page load still needs it; mutations record from their own response.
  const recordCartState = async () => {
    try {
      const res = await nativeFetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const hadSet = Array.isArray(cart.items) && cart.items.some(lineIsSet);
      write(HADSET_KEY, hadSet ? '1' : '0');
    } catch (e) {
      // Leave the previous value alone — guessing "no set" here would disarm the guard.
    }
  };

  // Derive the flag from the mutation body so we never refetch /cart.js on ATC.
  // /cart/add returns the added line (or items[]); writing "0" from a non-set
  // add would wipe a set that is already in the cart.
  const applyMutationPayload = (urlString, data) => {
    if (urlString.includes('/cart/clear')) {
      write(HADSET_KEY, '0');
      return;
    }
    if (!data || data.status) return;
    if (urlString.includes('/cart/add')) {
      const { items, variant_id: variantId } = data;
      const lines = Array.isArray(items) ? items : variantId ? [data] : [];
      if (lines.some(lineIsSet)) write(HADSET_KEY, '1');
      return;
    }
    const { items } = data;
    if (!Array.isArray(items)) return;
    write(HADSET_KEY, items.some(lineIsSet) ? '1' : '0');
  };

  const recordFromMutationResponse = (urlString, response) => {
    if (urlString.includes('/cart/clear')) {
      write(HADSET_KEY, '0');
      return;
    }
    response
      .clone()
      .json()
      .then((data) => applyMutationPayload(urlString, data))
      .catch(() => {});
  };

  /* ── watch every path that can mutate the cart ── */

  const installFetchHook = () => {
    const previous = window.fetch;
    window.fetch = function (url, ...rest) {
      const urlString = typeof url === 'string' ? url : (url?.url ?? '');
      const watched = isMutation(urlString);
      const result = previous.apply(this, [url, ...rest]);
      if (!watched) return result;
      return result.then((response) => {
        if (response && response.ok) recordFromMutationResponse(urlString, response);
        return response;
      });
    };
  };

  // Not every cart write in a Shopify storefront goes through fetch — app-owned and older
  // code still uses XHR, and those adds count just as much.
  const installXhrHook = () => {
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__skiSetUrl = String(url || '');
      this.__skiSetWatched = isMutation(this.__skiSetUrl);
      return open.call(this, method, url, ...rest);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (...args) {
      if (this.__skiSetWatched) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          if (this.__skiSetUrl.includes('/cart/clear')) {
            applyMutationPayload(this.__skiSetUrl, null);
            return;
          }
          try {
            applyMutationPayload(this.__skiSetUrl, JSON.parse(this.responseText));
          } catch (e) { /* non-JSON cart responses are ignored */ }
        });
      }
      return send.apply(this, args);
    };
  };

  /* ── the guard itself ── */

  const notify = (message) => {
    if (!message) return;
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, { source: 'ski-set-market-guard', message });
      }
    } catch (e) {
      /* the toast is a courtesy — never let it break the guard */
    }
  };

  const repaintCart = () => {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ski-set-market-guard' });
      }
    } catch (e) {
      /* fall through — the badge below is the part that must not stay wrong */
    }
    // <cart-items>/<cart-drawer-items> re-render off cartUpdate, but the count badge does not.
    nativeFetch(`${cartUrl()}?section_id=cart-icon-bubble`)
      .then(r => (r.ok ? r.text() : null))
      .then((html) => {
        if (!html) return;
        const target = document.getElementById('cart-icon-bubble');
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      })
      .catch(() => {});
  };

  async function run() {
    const now = marketKey();
    if (!now) return;

    const before = read(MARKET_KEY);

    // First visit of the session, or storage is blocked: record and stand down. With storage
    // blocked `read` always returns null, so this returns every time and the guard silently
    // never fires — deliberate. The backend (the Cart Transform refusing to reprice a set
    // whose `_price_currency` no longer matches) is the backstop.
    if (!before) {
      write(MARKET_KEY, now);
      recordCartState();
      return;
    }

    if (before === now) {
      // Same market — nothing to guard, but keep the flag current for the next page.
      recordCartState();
      return;
    }

    // The market changed. Only a cart that HELD a set is touched; ordinary carts are never
    // cleared, whatever market they move between.
    if (read(HADSET_KEY) !== '1') {
      write(MARKET_KEY, now);
      recordCartState();
      return;
    }

    let cleared = false;
    try {
      const res = await nativeFetch(`${root()}/cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      cleared = !!(res && res.ok);
    } catch (e) {
      cleared = false;
    }

    // A failed clear must NOT be recorded as success: leaving both flags set means the next
    // page load retries, rather than marking a still-poisoned cart clean.
    if (!cleared) return;

    write(MARKET_KEY, now);
    write(HADSET_KEY, '0');

    // Unlike a line that vanishes on its own, this removal is ours — and the shopper may
    // have been looking at the cart when it went. The copy says the whole cart went, because
    // it did: a line we cannot see is a line we cannot remove individually.
    notify(window.skiSetStrings?.marketCartCleared);
    repaintCart();
  }

  installFetchHook();
  installXhrHook();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
